import { AddCircleOutline } from '@mui/icons-material'
import { Box, Card, Chip, styled, Typography, useMediaQuery } from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import { useGetActiveEquipmentForLocality } from 'src/api/main-component'
import { useGetAllMaintenances } from 'src/api/maintenance'
import { useGetCagePositions, useGetMainComponentTypesForGroupBy } from 'src/api/scheduled-maintenance'
import { AppRoutesNavigation } from 'src/app-routes'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import ToggleGroupButtons from 'src/components/common/ToggleGroupButtons'
import { useNavigation } from 'src/context/NavigationContext'
import { MaintenanceViewMode } from 'src/enums'
import {
  EquipmentFilterOption,
  FilterOption,
  ScheduledMaintenanceFilter,
} from 'src/generated-types/scheduled-maintenance-filter'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { CreateCustomMaintenanceDialog } from './CreateCustomMaintenanceDialog'
import { StyledPerformedButton } from './LocalityMaintenanceContainer'
import MaintenanceFilter from './MaintenanceFilter'
import MaintenanceTable from './MaintenanceTable'
import { getMaintenanceHeaderKeyList } from './maintenanceTableHelper'
import ScheduledMaintenanceGroupedList from './ScheduledMaintenanceGroupedList'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import NoMaintenance from '../../../assets/svg/no-maintenances.svg'
import { FlexSpinner } from 'src/components/Spinner'

interface EquipmentMaintenanceContainerProps {
  onlyDaily?: boolean
}

const EquipmentMaintenanceContainer: FC<EquipmentMaintenanceContainerProps> = ({ onlyDaily }) => {
  const [viewMode, setViewMode] = useState<MaintenanceViewMode>(MaintenanceViewMode.GroupByCage)
  const { t } = useTranslation(['maintenance', 'common'])
  const { navigateTo } = useNavigation()
  const { customerId, localityId } = useParams()
  const { showDialog } = useDialogCtx()
  const { data: activeEquipment } = useGetActiveEquipmentForLocality(customerId as EntityId, localityId as EntityId)
  const [selectedMaintenanceId, setSelectedMaintenanceId] = useState<EntityId | null>(null)
  const location = useLocation()
  const isTabletScreen = useMediaQuery('(max-width:1630px)')

  useEffect(() => {
    const hash = location.hash
    if (hash && hash.startsWith('#maintenance-')) {
      const maintenanceId = hash.replace('#maintenance-', '')
      setSelectedMaintenanceId(maintenanceId as EntityId)
      setViewMode(MaintenanceViewMode.ShowAll)
    }
  }, [location.hash])

  const [filterOptions, setFilterOptions] = useState<ScheduledMaintenanceFilter>({
    cagePositionIds: [],
    typeIds: [],
    repeatTypes: [],
    mainComponentIds: [],
  })

  const filterIds = useMemo(
    () => ({
      repeatTypes: filterOptions?.repeatTypes?.map(rt => rt.id) || [],
      typeIds: filterOptions?.typeIds?.map(t => t.id) || [],
      mainComponentIds: filterOptions?.mainComponentIds?.map(mc => mc.id) || [],
      cagePositionIds: filterOptions?.cagePositionIds?.map(cp => cp.id) || [],
    }),
    [filterOptions],
  )

  const { data: cagePositions, isLoading: cagePositionsLoading } = useGetCagePositions(
    customerId as EntityId,
    localityId as EntityId,
    onlyDaily,
    filterIds,
    {
      enabled: viewMode === MaintenanceViewMode.GroupByCage,
    },
  )
  const { data: mainComponentTypes, isLoading: mainComponentTypeLoading } = useGetMainComponentTypesForGroupBy(
    customerId as EntityId,
    localityId as EntityId,
    onlyDaily,
    filterIds,
    {
      enabled: viewMode === MaintenanceViewMode.GroupByEquipment,
    },
  )
  const { data: maintenances, isLoading: maintenancesLoading } = useGetAllMaintenances(
    customerId as EntityId,
    localityId as EntityId,
    onlyDaily,
    filterIds,
    {
      enabled: viewMode === MaintenanceViewMode.ShowAll,
    },
  )

  const selectedData = viewMode === MaintenanceViewMode.GroupByCage ? cagePositions : mainComponentTypes
  const isLoadingData =
    viewMode === MaintenanceViewMode.ShowAll
      ? maintenancesLoading
      : viewMode === MaintenanceViewMode.GroupByCage
      ? cagePositionsLoading
      : mainComponentTypeLoading

  const onAdd = () => {
    showDialog(
      CreateCustomMaintenanceDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          mainComponents: activeEquipment || [],
          isLocalityMaintenance: false,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }

  const handleDelete = (category: keyof ScheduledMaintenanceFilter, value: FilterOption | EquipmentFilterOption) => {
    setFilterOptions(prev => {
      const updatedCategory = prev[category]?.filter(item => item !== value)
      return { ...prev, [category]: updatedCategory?.length ? updatedCategory : [] }
    })
  }

  return (
    <CommonPageLayout
      titleSection={{ title: t('equipment-maintenance', { ns: 'maintenance' }) }}
      topAction={[
        <>
          <ToggleGroupButtons
            key="actions"
            actions={[
              { title: t('show-all', { ns: 'maintenance' }), value: MaintenanceViewMode.ShowAll },
              { title: t('group-by-cage', { ns: 'maintenance' }), value: MaintenanceViewMode.GroupByCage },
              { title: t('group-by-equipment', { ns: 'maintenance' }), value: MaintenanceViewMode.GroupByEquipment },
            ]}
            defaultValue={MaintenanceViewMode.GroupByCage}
            value={viewMode}
            setViewMode={setViewMode}
            height="44px"
            buttonWidth="auto"
          />
          {!onlyDaily && (
            <StyledPerformedButton
              key="performed"
              variant="outlined"
              buttonText={t('history', { ns: 'common' })}
              onClick={() =>
                navigateTo(
                  AppRoutesNavigation.Localities.equipmentPerformedMaintenances(
                    customerId as EntityId,
                    localityId as EntityId,
                  ),
                )
              }
            />
          )}
        </>,
        <MaintenanceFilter key={'filter'} setFilterOptions={setFilterOptions} filterOptions={filterOptions} />,
        ...(!onlyDaily
          ? [
              {
                buttonText: isTabletScreen ? '' : t('add-maintenance', { ns: 'maintenance' }),
                buttonIcon: <AddCircleOutline />,
                variant: 'outlined',
                shadowWeight: 0,
                height: '44px',
                buttonTextProps: {
                  fontSize: '18px',
                },
                onClick: onAdd,
              },
            ]
          : []),
      ]}
    >
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', paddingY: 1 }}>
        {Object.entries(filterOptions).flatMap(([category, values]) =>
          values
            ? values.map(value => (
                <StyledFilteredItemChip
                  key={`${category}-${value.id}`}
                  label={`${value.category} : ${value.name}`}
                  onDelete={() => handleDelete(category as keyof ScheduledMaintenanceFilter, value)}
                />
              ))
            : [],
        )}
      </Box>
      <StyledCard
        className="p-3 mb-2"
        variant={
          !onlyDaily
            ? 'elevation'
            : isLoadingData || (selectedData && selectedData.length > 0)
            ? 'elevation'
            : 'outlined'
        }
      >
        <Flex.Row paddingLeft={'60px'} paddingBottom={'12px'} paddingTop={'10px'}>
          {getMaintenanceHeaderKeyList(t).map((item: { flex: number; headerName: string }, index) => (
            <Typography flex={item?.flex} key={index}>
              {item.headerName}
            </Typography>
          ))}
        </Flex.Row>

        {viewMode === MaintenanceViewMode.ShowAll ? (
          isLoadingData ? (
            <FlexSpinner />
          ) : maintenances && maintenances.length > 0 ? (
            <MaintenanceTable
              maintenances={maintenances}
              selectedItemId={localityId as EntityId}
              selectedMaintenanceId={selectedMaintenanceId}
              category={MaintenanceViewMode.ShowAll}
              onlyDaily={onlyDaily}
              filterIds={filterIds}
            />
          ) : (
            <Box className="text-center">
              <EmptyStatusCard
                image={NoMaintenance}
                header={t('no-maintenance-title', { ns: 'maintenance' })}
                content={t('no-maintenance-content', { ns: 'maintenance' })}
                imageWidth="60px"
                headerFontSize="18px"
                contentFontSize="14px"
                contentTopMargin="0px"
                compact={true}
              />
            </Box>
          )
        ) : (
          <ScheduledMaintenanceGroupedList
            dataList={selectedData}
            isLoading={isLoadingData}
            viewMode={viewMode}
            onlyDaily={onlyDaily as boolean}
            filterIds={filterIds}
          />
        )}
      </StyledCard>
    </CommonPageLayout>
  )
}

export default EquipmentMaintenanceContainer

interface StyledCardProps {
  height?: string
}

export const StyledCard = styled(Card)<StyledCardProps>(({ height }) => ({
  height: height,
  overflowY: 'hidden',
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: 'none',
  border: 'none',
}))

export const StyledFilteredItemChip = styled(Chip)(({ theme }) => ({
  backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
  color: getThemeColor(theme, MUIThemeColors.white),
  borderRadius: '4px',
  '& .MuiChip-deleteIcon': {
    color: getThemeColor(theme, MUIThemeColors.white),
  },
}))
